import React, { useEffect, useState, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/en-gb';
import {
  Table, Typography, Row, Button, Col, Radio,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { selectPackages } from '../../../../state/domain/packages/packageReducer';
import { putPackagePurchaseRange, requestAllUsageSummaries, requestNetUsageSummaries } from '../../../../state/ui/configuration/packages/packageUiActions';
import { selectFetchUsageSummariesRequest } from '../../../../state/ui/configuration/packages/packageUiReducer';
import './PackageSales.css';

const { Column } = Table;
const { Text } = Typography;

// TABLE
function SummaryTable({
  isLoading, packages, reloadData, showNetValues,
}) {
  const [visiblePackages, setVisiblePackages] = useState([]);

  const intl = useIntl();
  const RowTotal = () => (
    <div>
      <Text>{`${visiblePackages.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // Keeps track of the current filtered month in order to update the column header and trigger the below useEffect.
  // Stores the month zero indesxed since this is what moment does.
  // Even though filterMultiple is false, this still has to be an array.
  const [filteredMonth, setFilteredMonth] = useState([moment.utc().subtract(1, 'M').month()]);

  const [filteredCurrency, setFilteredCurrency] = useState(['GBP']);
  // Protects against bad filters: I.E reset. Reverts to default if any bad data is sent in.
  const safeSetFilteredMonth = newVal => (
    setFilteredMonth(newVal !== null && newVal.length === 1 ? newVal : [moment.utc().subtract(1, 'M').month()])
  );
  // reloads the table data with the custom data for the given month.
  useEffect(() => {
    // Returns the zero-indexed month
    const nowMonth = moment.utc().month();
    const fromDate = moment.utc().month(filteredMonth[0]).startOf('M');
    const toDate = moment.utc().month(filteredMonth[0]).endOf('M');
    // This query does usages >= fromDate and < toDate so it works from month start to month start.
    reloadData(
      nowMonth > filteredMonth[0] ? fromDate.toISOString() : fromDate.subtract(1, 'y').toISOString(),
      nowMonth > filteredMonth[0] ? toDate.toISOString() : toDate.subtract(1, 'y').toISOString(),
    );
  }, [filteredMonth, reloadData]);

  useEffect(() => {
    const tempPackages = packages.slice();
    setVisiblePackages(tempPackages.map(strPackage => ({
      ...strPackage,
      price: strPackage.price.toFixed(2),
      vatCharge: strPackage.vatCharge ? strPackage.vatCharge.toFixed(2) : 'N/A',
      pastDay: showNetValues ? strPackage.pastDay.toFixed(2) : strPackage.pastDay,
      pastWeek: showNetValues ? strPackage.pastWeek.toFixed(2) : strPackage.pastWeek,
      pastMonth: showNetValues ? strPackage.pastMonth.toFixed(2) : strPackage.pastMonth,
      pastYear: showNetValues ? strPackage.pastYear.toFixed(2) : strPackage.pastYear,
      customRange: showNetValues ? strPackage.customRange.toFixed(2) : strPackage.customRange,
      total: showNetValues ? strPackage.total.toFixed(2) : strPackage.total,
    })));
  }, [packages, showNetValues]);

  function sortByTitle(a, b) {
    const aStr = `${a.title}`;
    const bStr = `${b.title}`;
    return aStr.localeCompare(bStr);
  }

  function sortByNumber(a, b, dataIndex) {
    if (a[dataIndex] && !b[dataIndex]) return 1;
    if (!a[dataIndex] && b[dataIndex]) return -1;
    if (a[dataIndex] === b[dataIndex]) return 0;
    return a[dataIndex] > b[dataIndex] ? 1 : -1;
  }

  // Retrieves the list of months ascending startign from last month
  const monthOptions = useMemo(() => {
    const monthList = [];
    for (let i = 1; i <= 12; i += 1) {
      // The 0 indexed month
      const monthIndex = moment.utc().subtract(i, 'M').month();
      monthList.push({ value: monthIndex, text: intl.formatMessage({ id: `month${monthIndex}` }) });
    }
    return monthList;
  }, [intl]);

  const currencyOptions = [{ value: 'GBP', text: 'GBP' }, { value: 'USD', text: 'USD' }, { value: 'EUR', text: 'EUR' }];

  function handleFilters(filters) {
    if (filters.customRange && filters.customRange[0] !== filteredMonth[0]) safeSetFilteredMonth(filters.customRange);
    if (filters.currencyCode && filters.currencyCode[0] !== filteredCurrency[0]) setFilteredCurrency(filters.currencyCode);
  }

  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visiblePackages}
      onChange={(pagination, filters) => handleFilters(filters)}
      rowKey={record => record.code}
      expandIcon={({ expanded, record, onExpand }) => (
        <Row type="flex" align="middle" className="expandable-icon">
          <Button type="link" size="small" onClick={e => onExpand(record, e)}>
            <FormattedMessage id={expanded ? 'hide' : 'view'} />
          </Button>
        </Row>
      )}
      expandIconAsCell={false}
      expandIconColumnIndex={0}
      expandedRowRender={record => (
        <Row type="flex" align="middle" justify="space-around">
          <Col span={6}>
            <Row type="flex" align="middle" justify="space-around">
              <Col span={16}>
                <b><FormattedMessage id="purchasedCredits" /></b>
                :&nbsp;
              </Col>
              <Col span={8}>
                {record.mainTokens}
              </Col>
            </Row>
          </Col>
          <Col span={6}>
            <Row type="flex" align="middle" justify="space-around">
              <Col span={16}>
                <b><FormattedMessage id="bonusCredits" /></b>
                :&nbsp;
              </Col>
              <Col span={8}>
                {record.extraTokens}
              </Col>
            </Row>
          </Col>
          <Col span={6}>
            <Row type="flex" align="middle" justify="space-around">
              <Col span={16}>
                <b><FormattedMessage id="price" /></b>
                :&nbsp;
              </Col>
              <Col span={8}>
                {`${record.price} ${record.currencyCode}`}
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    >
      <Column
        sorter={sortByTitle}
        defaultSortOrder="ascend"
        title={<FormattedMessage id="packageName" />}
        dataIndex="title"
        key="title"
        className="expandable-col"
        render={text => (
          <div className="expandable-col-content">{text}</div>
        )}
      />
      <Column
        title={<FormattedMessage id="currency" />}
        dataIndex="currencyCode"
        key="currencyCode"
        filtered
        filterMultiple={false}
        filters={currencyOptions}
        filteredValue={filteredCurrency}
        onFilter={(value, record) => record.currencyCode === value}
        align="center"
      />
      <Column
        title={<FormattedMessage id="utilisedMonths" values={{ month: intl.formatMessage({ id: `month${filteredMonth[0]}` }) }} />}
        dataIndex="customRange"
        key="customRange"
        filtered
        filteredValue={filteredMonth}
        filterMultiple={false}
        filters={monthOptions}
        sorter={(a, b) => sortByNumber(a, b, 'customRange')}
        render={text => (
          <Typography.Text>{text || 0}</Typography.Text>
        )}
        align="right"
      />
      <Column
        title={<FormattedMessage id="utilisedTotal" />}
        dataIndex="total"
        key="total"
        sorter={(a, b) => sortByNumber(a, b, 'total')}
        render={text => (
          <Typography.Text>{text || 0}</Typography.Text>
        )}
        align="right"
      />
      <Column
        title={<FormattedMessage id="utilised1" />}
        dataIndex="pastDay"
        key="pastDay"
        sorter={(a, b) => sortByNumber(a, b, 'pastDay')}
        render={text => (
          <Typography.Text>{text || 0}</Typography.Text>
        )}
        align="right"
      />
      <Column
        title={<FormattedMessage id="utilised7" />}
        dataIndex="pastWeek"
        key="pastWeek"
        sorter={(a, b) => sortByNumber(a, b, 'pastWeek')}
        render={text => (
          <Typography.Text>{text || 0}</Typography.Text>
        )}
        align="right"
      />
      <Column
        title={<FormattedMessage id="utilised28" />}
        dataIndex="pastMonth"
        key="pastMonth"
        sorter={(a, b) => sortByNumber(a, b, 'pastMonth')}
        render={text => (
          <Typography.Text>{text || 0}</Typography.Text>
        )}
        align="right"
      />
      <Column
        title={<FormattedMessage id="utilised365" />}
        dataIndex="pastYear"
        key="pastYear"
        sorter={(a, b) => sortByNumber(a, b, 'pastYear')}
        render={text => (
          <Typography.Text>{text || 0}</Typography.Text>
        )}
        align="right"
      />
    </Table>
  );
}

SummaryTable.propTypes = {
  isLoading: PropTypes.bool,
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      packageEndDate: PropTypes.string,
      currencyCode: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      mainTokens: PropTypes.number.isRequired,
      extraTokens: PropTypes.number,
      active: PropTypes.bool.isRequired,
    }),
  ),
  reloadData: PropTypes.func.isRequired,
  showNetValues: PropTypes.bool.isRequired,
};

SummaryTable.defaultProps = {
  isLoading: false,
  packages: [],
};

function StateSelector({ setShowNetValues, showNetValues }) {
  const changeShowNetValues = (value) => {
    setShowNetValues(value.target.value === 'net');
  };
  return (
    <Radio.Group value={showNetValues ? 'net' : 'quantity'} onChange={changeShowNetValues} buttonStyle="solid" style={{ margin: '10px' }}>
      <Radio.Button value="net"><FormattedMessage id="value" /></Radio.Button>
      <Radio.Button value="quantity"><FormattedMessage id="quantity" /></Radio.Button>
    </Radio.Group>
  );
}

StateSelector.propTypes = {
  setShowNetValues: PropTypes.func.isRequired,
  showNetValues: PropTypes.bool.isRequired,
};

function CreditUtilization({
  isLoading, loadQuantityData, packages, loadNetData,
}) {
  // Page data is loaded by the table since it requires filter knowledge
  const [showNetValues, setShowNetValues] = useState(false);

  // Controls which method is used to populate the table
  // This works, You have to set functions in a weird way using useState, I don't know why.
  // Thats why it is set to () => loadQuantityData rather than just loadQuantityData
  // The same thing applies with onClick, onChange etc... Still don't understand why though!
  const [reloadData, setReloadData] = useState(() => loadQuantityData);
  useEffect(() => { setReloadData(() => (showNetValues ? loadNetData : loadQuantityData)); },
    [setReloadData, showNetValues, loadNetData, loadQuantityData]);

  return (
    <ConfigHeading
      titleId="creditUtilisation"
    >
      <StateSelector setShowNetValues={setShowNetValues} showNetValues={showNetValues} />
      <SummaryTable
        isLoading={isLoading}
        packages={packages}
        reloadData={reloadData}
        showNetValues={showNetValues}
      />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
CreditUtilization.propTypes = {
  isLoading: PropTypes.bool,
  loadQuantityData: PropTypes.func.isRequired,
  loadNetData: PropTypes.func.isRequired,
  packages: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    packageEndDate: PropTypes.string,
    currencyCode: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    mainTokens: PropTypes.number.isRequired,
    extraTokens: PropTypes.number,
    active: PropTypes.bool.isRequired,
    registration: PropTypes.bool,
  })),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

CreditUtilization.defaultProps = {
  isLoading: false,
  packages: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectFetchUsageSummariesRequest(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    packages: selectPackages(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadQuantityData: (fromDate, toDate) => dispatch(requestAllUsageSummaries(fromDate, toDate)),
  loadNetData: (fromDate, toDate) => dispatch(requestNetUsageSummaries(fromDate, toDate)),
  setPackagePurchaseRange: (packageId, fromDateString, toDateString) => dispatch(
    putPackagePurchaseRange({ [packageId]: { fromDateString, toDateString } }),
  ),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(injectIntl(CreditUtilization)));
